/**
 *  displays store locations with option to  navigate through alphabet list
 *
 * @module views/components/IndexedStores
 * @memberof -Common
 */
import './IndexedStores.scss';

import React, { useCallback } from 'react';

import PropTypes from 'prop-types';

import Text from '@ulta/core/components/Text/Text';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import AnchorList from '@ulta/components/AnchorList/AnchorList';
import Divider from '@ulta/components/Divider/Divider';
import IndexedStoreCard from '@ulta/components/IndexedStoreCard/IndexedStoreCard';

import * as utils from '@ulta/hooks/useAnchorList/useAnchorList';

/**
 * Represents a IndexedStores component
 *
 * @method
 * @param { IndexedStoresProps} props - React properties passed from composition
 * @returns IndexedStores
 */
export const IndexedStores = function( props ){
  const { title, items, oddBackgroundColor, evenBackgroundColor, storeAccessibilityLabel } = props;
  const {
    reportItemScrolling,
    shouldOffsetElement,
    headerOffset,
    onItemScrolled,
    positionElementRef,
    offsetElementRef,
    setScrollTarget,
    selectedLetter,
    setSelectedLetter,
    setReportItemScrolling } = utils.useAnchorList();


  const onAnchorLetterClick = useCallback(
    onAnchorLetterClicked(
      { items },
      { setScrollTarget, setSelectedLetter, setReportItemScrolling }
    ),
    []
  );

  if( !items ){
    return null;
  }

  const style = shouldOffsetElement ? { transform: `translateY(${headerOffset}px)` } : undefined;

  return (
    <div className='IndexedStores'>
      <div className='IndexedStores--anchorListPositionEl'
        ref={ positionElementRef }
      />
      <div
        ref={ offsetElementRef }
        className='IndexedStores--anchorListWrapper'
        { ...spreadProps( { style } ) }
      >
        <AnchorList
          allBrandsTitle={ title }
          navItems={ items }
          selectedLetter={ selectedLetter }
          onLetterClick={ ( letter ) => {
            onAnchorLetterClick( letter, 0 );
          } }
        />
      </div>
      { items.map( ( indexedStores, index ) => {
        if( indexedStores?.items?.length < 1 ){
          return null;
        }
        return (
          <div key={ index }>
            <div
              className='IndexedStores--alphabetIndex'
              id={ `${indexedStores?.title}` === '#' ? `Hashsection` : `${indexedStores?.title}section` }
            >
              <Text htmlTag='h3'
                textAlign='left'
                textStyle='title-6'
              >
                { indexedStores.title }
              </Text>
            </div>
            <Divider type='gray' />
            { indexedStores?.items?.map( ( store, index ) => {
              return (
                <div key={ store.title }>
                  <div className='IndexedStores--stateName'>
                    <Text htmlTag='h4'
                      textAlign='left'
                      textStyle='body-2'
                    >
                      { store.title }
                    </Text>
                  </div>
                  <IndexedStoreCard
                    key={ index }
                    indexedStores={ indexedStores }
                    store={ store }
                    onScrolled={ reportItemScrolling && onItemScrolled }
                    reportActive={ reportItemScrolling }
                    oddBackgroundColor={ oddBackgroundColor?.toLowerCase() }
                    evenBackgroundColor={ evenBackgroundColor?.toLowerCase() }
                    storeAccessibilityLabel={ storeAccessibilityLabel }
                  />
                </div>
              );
            } ) }
          </div>
        );
      } ) }
    </div>
  );
};

/**
 * action on anchor letter clicked
 * @method onAnchorLetterClicked
 * @param {object} data - Data arguments
 * @param {*} methods.setSelectedLetter - React useState setter for letter
 * @param {*} methods.setScrollTarget - React useState setter for scroll target
 * @param {*} methods.setReportItemScrolling - React useState setter when items are getting scrolled
 * @returns {function} onAnchorLetterClick callback
 */
export const onAnchorLetterClicked = ( data, methods )=>{
  const { items } = handleEmptyObjects( data );
  const {
    setScrollTarget,
    setSelectedLetter,
    setReportItemScrolling
  } = handleEmptyObjects( methods );
  const onAnchorLetterClick = ( letter ) => {
    if( !letter || !items ){
      return;
    }
    setScrollTarget( '' );
    setSelectedLetter( letter );
    setScrollTarget( utils.getSectionId( { letter } ) );
    utils.pauseItemScrollReporting( { delay: utils.SCROLL_REPORTING_ONCLICK_DELAY }, {
      setReportItemScrolling,
      callback: () => setScrollTarget( '' )
    } );
  };

  return onAnchorLetterClick;
};


/**
 * Property type definitions
 * @typedef IndexedStores
 * @type {object}
 * @property {string} title - Sets the title of the items
 * @property {array} items - List of All store to display
 * @property {string} oddBackgroundColor - sets background color for cards
 * @property {string} evenBackgroundColor -  sets background color for cards
 * @property {string} storeAccessibilityLabel - Sets the Aria Label of the all stores
 */
export const propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape( {
      title: PropTypes.string.isRequired,
      action: PropTypes.object,
      items: PropTypes.arrayOf(
        PropTypes.shape( {
          title: PropTypes.string.isRequired,
          items: PropTypes.arrayOf(
            PropTypes.shape( {
              cityName: PropTypes.string.isRequired,
              storeName: PropTypes.string.isRequired,
              address1: PropTypes.string,
              address2: PropTypes.string
            } ).isRequired
          )
        } )
      ).isRequired
    } )
  ),
  oddBackgroundColor: PropTypes.string,
  evenBackgroundColor: PropTypes.string,
  storeAccessibilityLabel: PropTypes.string
};


IndexedStores.propTypes = propTypes;

export default IndexedStores;
